import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BadgeCheck, MapPin, Plus, Save, Trash2 } from "lucide-react";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddressDialog } from "@/components/order-type/AddressDialog";
import { OrderCard } from "@/components/orders/OrderCard";
import {
  EmptyState,
  ErrorState,
  ProductSkeletonList,
  Spinner,
} from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { loyaltyWalletQuery, ordersQuery } from "@/lib/queries";
import { loc } from "@/lib/format";
import { customerErrorMessage } from "@/lib/customer-error";
import { googleMapsUrl } from "@/lib/maps";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import type { Address } from "@/lib/types";
import { toast } from "sonner";
import { AccountLoyaltyBalance } from "@/components/account/AccountLoyaltyBalance";
import { featureEnabled, useStorefront } from "@/lib/storefront";

export const Route = createFileRoute("/account")({
  component: AccountPage,
});

function AccountPage() {
  const { t, lang } = useI18n();
  const storefront = useStorefront();
  const { user, updateProfile, deleteAddress } = useAuth();
  const { ready } = useRequireAuth();
  const history = useQuery({ ...ordersQuery(user?.id ?? ""), enabled: ready });
  const loyaltyAvailable =
    storefront.mode !== "preview" && featureEnabled(storefront, "loyalty");
  const loyalty = useQuery({
    ...loyaltyWalletQuery(user?.id ?? ""),
    enabled: ready && loyaltyAvailable,
  });
  const orders = history.data?.slice(0, 3) ?? [];
  const [editing, setEditing] = useState<Address | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<Address | null>(null);
  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    setName(user?.name ?? "");
  }, [user?.name]);

  if (!ready || !user) {
    return (
      <PageShell size="narrow">
        <ProductSkeletonList count={2} />
      </PageShell>
    );
  }

  const profileChanged = name.trim() !== (user.name ?? "");

  async function saveProfile(event: React.SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (savingProfile || !profileChanged) return;
    setSavingProfile(true);
    try {
      await updateProfile({ name });
      toast.success(t("common.saved"));
    } catch (error) {
      toast.error(customerErrorMessage(error, t));
    } finally {
      setSavingProfile(false);
    }
  }

  return (
    <PageShell size="narrow">
      <PageHeading
        breadcrumbs={[
          { label: t("nav.home"), to: "/" },
          { label: t("account.title") },
        ]}
        title={t("account.title")}
        description={t("account.subtitle")}
      />

      <form
        onSubmit={saveProfile}
        className="surface-card mb-8 grid gap-5 p-4 sm:p-6"
      >
        <h2 className="text-lg font-bold">{t("account.profileTitle")}</h2>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xl font-black">
              <bdi>{user.name || user.phone}</bdi>
            </p>
            <p className="text-sm text-muted-foreground">
              <bdi dir="ltr">{user.phone}</bdi>
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              user.verified
                ? "bg-success/15 text-success"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <BadgeCheck className="size-4" aria-hidden="true" />
            {user.verified ? t("account.verified") : t("account.unverified")}
          </span>
        </div>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="profile-name">{t("account.name")}</Label>
            <Input
              id="profile-name"
              autoComplete="name"
              required
              minLength={2}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>
        <Button
          type="submit"
          className="w-full sm:w-fit"
          disabled={savingProfile || name.trim().length < 2 || !profileChanged}
        >
          {savingProfile ? <Spinner /> : <Save className="size-4" />}
          {t(savingProfile ? "common.saving" : "common.save")}
        </Button>
      </form>

      {loyaltyAvailable && loyalty.data?.program ? (
        <AccountLoyaltyBalance
          availablePoints={loyalty.data.account.availablePoints}
        />
      ) : null}

      <section className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">{t("account.addresses")}</h2>
          <Button
            size="sm"
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" />
            {t("account.addAddress")}
          </Button>
        </div>

        {user.addresses.length === 0 ? (
          <EmptyState
            title={t("account.noAddresses")}
            description={t("account.noAddressesHint")}
            icon={<MapPin className="size-7" />}
          />
        ) : (
          <div className="grid gap-3">
            {user.addresses.map((a) => (
              <article
                key={a.id}
                className="surface-card flex flex-wrap items-start justify-between gap-3 p-5"
              >
                <div className="text-sm">
                  <p className="font-bold">{loc(a.label, lang)}</p>
                  <p className="text-muted-foreground">
                    {[
                      a.street,
                      a.area,
                      a.building && `${t("orderType.building")} ${a.building}`,
                      a.floor && `${t("orderType.floor")} ${a.floor}`,
                      a.apartment &&
                        `${t("orderType.apartment")} ${a.apartment}`,
                    ]
                      .filter(Boolean)
                      .join(lang === "ar" ? "، " : ", ")}
                  </p>
                  <p className="text-muted-foreground">
                    <bdi dir="ltr">{a.phone}</bdi>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="ghost">
                    <a href={googleMapsUrl(a)} target="_blank" rel="noreferrer">
                      <MapPin className="size-4" />
                      {t("account.openMaps")}
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditing(a);
                      setDialogOpen(true);
                    }}
                  >
                    {t("account.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t("account.deleteAddress")}
                    onClick={() => setDeleting(a)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">{t("account.recentOrders")}</h2>
          <Button asChild size="sm" variant="ghost">
            <Link to="/orders">{t("account.viewOrders")}</Link>
          </Button>
        </div>
        {history.isPending ? (
          <ProductSkeletonList count={2} />
        ) : history.isError ? (
          <ErrorState
            error={history.error}
            message={t("orders.loadError")}
            onRetry={() => history.refetch()}
          />
        ) : orders.length === 0 ? (
          <EmptyState
            title={t("orders.empty")}
            description={t("orders.emptyDesc")}
          />
        ) : (
          <div className="grid gap-4">
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} />
            ))}
          </div>
        )}
      </section>

      <AddressDialog
        key={`${editing?.id ?? "new"}-${dialogOpen ? "open" : "closed"}`}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        address={editing}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("account.deleteAddress")}
        description={t("account.deleteAddressDescription")}
        confirmLabel={t("account.deleteAddress")}
        onConfirm={() => {
          if (!deleting) return;
          void deleteAddress(deleting.id)
            .then(() => {
              toast.success(t("account.addressDeleted"));
              setDeleting(null);
            })
            .catch((error) => toast.error(customerErrorMessage(error, t)));
        }}
      />
    </PageShell>
  );
}
